"use client";

import * as React from "react";
import Image from "next/image";
import clsx from "clsx";
import type { CarouselApi } from "@relume_io/relume-ui";
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious,
} from "@relume_io/relume-ui";
import { LazyMotion, domAnimation, m, type MotionProps } from "framer-motion";

export type GalleryImage = { src: string; alt: string };

type BaseProps = { images: GalleryImage[] };

type ConflictingHtmlEvents =
    | "onAnimationStart"
    | "onAnimationEnd"
    | "onAnimationIteration"
    | "onDrag"
    | "onDragStart"
    | "onDragEnd"
    | "onDragEnter"
    | "onDragLeave"
    | "onDragOver"
    | "onDrop";

type SafeSectionHtmlProps = Omit<
    React.ComponentPropsWithoutRef<"section">,
    keyof BaseProps | keyof MotionProps | ConflictingHtmlEvents
>;

export type GalleryAttractionSectionClientProps = BaseProps & SafeSectionHtmlProps & MotionProps;

const slideUp = {
    hidden: { y: 30, opacity: 0 },
    visible: {
        y: 0,
        opacity: 1,
        transition: { duration: 0.8, ease: [0.215, 0.61, 0.355, 1] },
    },
} as const;

const reveal = {
    hidden: { clipPath: "inset(0% 0% 100% 0%)" },
    visible: (i: number) => ({
        clipPath: "inset(0% 0% 0% 0%)",
        transition: {
            duration: 1.2,
            ease: [0.45, 0, 0.55, 1],
            delay: i * 0.1 + 0.2,
        },
    }),
} as const;

export default function GalleryAttractionSectionClient({
                                                           images,
                                                           className,
                                                           ...rest
                                                       }: GalleryAttractionSectionClientProps) {
    const [api, setApi] = React.useState<CarouselApi | undefined>(undefined);

    const carouselOpts = React.useMemo(() => ({ loop: true, align: "start" as const }), []);
    const sizes = "(min-width: 768px) 50vw, 100vw";

    React.useEffect(() => {
        if (!api) return;

        const onSelect = () => {};

        api.on("select", onSelect);

        return () => {
            api.off("select", onSelect);
        };
    }, [api]);

    if (!images?.length) return null;

    return (
        <LazyMotion features={domAnimation} strict>
            <m.section
                id="relume"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                className={clsx("overflow-hidden px-[5%] py-16 md:py-24 lg:py-28", className)}
                {...rest}
            >
                <div className="container">
                    <m.div variants={slideUp}>
                        <Carousel setApi={setApi} opts={carouselOpts}>
                            <CarouselContent className="ml-0">
                                {images.map((image, index) => (
                                    <CarouselItem
                                        key={`${image.src}-${index}`}
                                        className="basis-full pl-0 pr-6 md:basis-1/2 md:pr-8"
                                    >
                                        <m.div
                                            variants={reveal}
                                            custom={index}
                                            className="relative aspect-square w-full overflow-hidden rounded-3xl"
                                        >
                                            <Image
                                                src={image.src}
                                                alt={image.alt || ""}
                                                fill
                                                sizes={sizes}
                                                className="object-cover transition-transform duration-700 hover:scale-105"
                                                draggable={false}
                                                decoding="async"
                                                priority={index === 0}
                                                loading={index === 0 ? "eager" : "lazy"}
                                                fetchPriority={index === 0 ? "high" : "low"}
                                            />
                                        </m.div>
                                    </CarouselItem>
                                ))}
                            </CarouselContent>

                            <m.div variants={slideUp} className="rt-8 mt-8 flex items-center justify-end">
                                <div className="flex items-end justify-end gap-2 md:gap-4 mt-6">
                                    <CarouselPrevious className="static right-0 top-0 size-10 rounded-full border bg-primary-light border-primary-light hover:bg-accent hover:text-primary-light transition duration-500" />
                                    <CarouselNext className="static right-0 top-0 size-10 rounded-full border bg-primary-light border-primary-light hover:bg-accent hover:text-primary-light transition duration-500" />
                                </div>
                            </m.div>
                        </Carousel>
                    </m.div>
                </div>
            </m.section>
        </LazyMotion>
    );
}